import {
	HttpBackend,
	HttpClient,
	HttpEventType,
	HttpHeaders,
	HttpRequest,
	HttpResponse,
} from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import * as awsS3Service from '@services/aws-s3';
import { UUID } from 'angular2-uuid';
import { CONSTANTS } from '../../app.constants';
import { AuthService } from '../authentication/auth.service';
import { ConfigService } from '../config/config.service';

@Injectable()
export class AwsS3Service {
	private direct: HttpClient;

	public progress: EventEmitter<any> = new EventEmitter();

	public readonly MULTIPART_THRESHOLD = 20 * 1024 * 1024; // 20 MB: Files >= 20MB use multipart upload
	public readonly CHUNK_SIZE = 5 * 1024 * 1024;           // 5 MB: S3 requires at least 5MB per part (except last)
	public readonly MAX_CONCURRENT_PARTS = 3;               // Optimal parallelism: max 3 simultaneous HTTP PUT connections to S3
	// Maximum retry attempts for an individual 5MB part (gives 5 total attempts: 1 initial + 4 retries).
	// If all 4 retries fail for any single part, the entire multipart upload is aborted and all
	// previously uploaded parts are purged from AWS S3 to prevent orphan storage costs.
	public readonly MAX_PART_RETRIES = 4;
	// Maximum retry attempts for a single PUT presigned URL upload (< 20MB) upon network drop or timeout
	// (gives 4 total attempts: 1 initial + 3 retries) with progressive backoff delay.
	public readonly MAX_SINGLE_UPLOAD_RETRIES = 3;

	constructor(
		private auth: AuthService,
		private config: ConfigService,
		private handler: HttpBackend,
		private http: HttpClient,
	) {
		this.direct = new HttpClient(this.handler);
	}

	private _httpUploadProgress = (filename: any, progress: any): void => {
		const toBeEmitted = {
			progress: progress,
			name: filename,
		};
		this.progress.emit(toBeEmitted);
	};

	public requestPresignedUploadUrl(payload: any): Promise<any> {
		const url = `${this.config.getBasePath()}/orders/upload/presigned-url`;
		const httpOptions = {
			headers: new HttpHeaders({
				Authorization: 'Bearer ' + this.auth.getToken(),
				'Content-Type': 'application/json',
			}),
		};
		return this.direct
			.post(url, payload, httpOptions)
			.toPromise()
			.then((response: any) => response)
			.catch((error: any) => {
				throw error.error || error;
			});
	}

	public requestInitiateMultipartUpload(payload: any): Promise<any> {
		const url = `${this.config.getBasePath()}/orders/upload/multipart/initiate`;
		const httpOptions = {
			headers: new HttpHeaders({
				Authorization: 'Bearer ' + this.auth.getToken(),
				'Content-Type': 'application/json',
			}),
		};
		return this.direct
			.post(url, payload, httpOptions)
			.toPromise()
			.then((response: any) => response)
			.catch((error: any) => {
				throw error.error || error;
			});
	}

	public requestCompleteMultipartUpload(payload: any): Promise<any> {
		const url = `${this.config.getBasePath()}/orders/upload/multipart/complete`;
		const httpOptions = {
			headers: new HttpHeaders({
				Authorization: 'Bearer ' + this.auth.getToken(),
				'Content-Type': 'application/json',
			}),
		};
		return this.direct
			.post(url, payload, httpOptions)
			.toPromise()
			.then((response: any) => response)
			.catch((error: any) => {
				throw error.error || error;
			});
	}

	public requestAbortMultipartUpload(payload: any): Promise<any> {
		const url = `${this.config.getBasePath()}/orders/upload/multipart/abort`;
		const httpOptions = {
			headers: new HttpHeaders({
				Authorization: 'Bearer ' + this.auth.getToken(),
				'Content-Type': 'application/json',
			}),
		};
		return this.direct
			.post(url, payload, httpOptions)
			.toPromise()
			.then((response: any) => response)
			.catch((error: any) => {
				console.warn('Error aborting multipart upload:', error);
				return { aborted: false };
			});
	}

	public uploadToPresignedUrl(
		uploadUrl: string,
		file: File | Blob,
		contentType?: string,
		contentDisposition?: string,
		retriesLeft: number = this.MAX_SINGLE_UPLOAD_RETRIES,
	): Promise<any> {
		return new Promise((resolve, reject) => {
			const mimeType = contentType || file.type || 'application/octet-stream';
			let headersConfig: { [name: string]: string } = {
				'Content-Type': mimeType,
			};
			if (contentDisposition) {
				headersConfig['Content-Disposition'] = contentDisposition;
			}
			const req = new HttpRequest('PUT', uploadUrl, file, {
				headers: new HttpHeaders(headersConfig),
				reportProgress: true,
			});

			this.direct.request(req).subscribe({
				next: (event: any) => {
					if (event.type === HttpEventType.UploadProgress) {
						this._httpUploadProgress((file as File).name || 'file', {
							loaded: event.loaded,
							total: event.total || file.size,
						});
					} else if (event instanceof HttpResponse) {
						resolve(event);
					}
				},
				error: (err: any) => {
					if (retriesLeft > 0) {
						const delayMs = (this.MAX_SINGLE_UPLOAD_RETRIES - retriesLeft + 1) * 1000;
						setTimeout(() => {
							this.uploadToPresignedUrl(uploadUrl, file, contentType, contentDisposition, retriesLeft - 1)
								.then(resolve)
								.catch(reject);
						}, delayMs);
					} else {
						reject(err);
					}
				},
			});
		});
	}

	/**
	 * Uploads an individual 5MB chunk directly to AWS S3 via presigned PUT URL.
	 * If the chunk upload fails due to network drop or timeout, it automatically retries
	 * up to MAX_PART_RETRIES (4 retries = 5 attempts total) with increasing backoff delay.
	 * Extracts the S3 ETag from response headers upon success.
	 */
	public uploadPartWithRetry(
		part: { partNumber: number; uploadUrl: string },
		chunkBlob: Blob,
		filename: string,
		onProgress: (loaded: number) => void,
		retriesLeft: number = this.MAX_PART_RETRIES,
	): Promise<{ partNumber: number; etag: string }> {
		return new Promise((resolve, reject) => {
			const req = new HttpRequest('PUT', part.uploadUrl, chunkBlob, {
				headers: new HttpHeaders({
					'Content-Type': 'application/octet-stream',
				}),
				reportProgress: true,
			});

			this.direct.request(req).subscribe({
				next: (event: any) => {
					if (event.type === HttpEventType.UploadProgress) {
						onProgress(event.loaded);
					} else if (event instanceof HttpResponse) {
						onProgress(chunkBlob.size);
						const rawEtag = event.headers.get('ETag') || event.headers.get('etag') || '';
						const etag = rawEtag.replace(/"/g, '').trim();
						if (!etag) {
							reject(
								new Error(
									`ETag header missing for part ${part.partNumber}. Ensure ETag is exposed in S3 bucket CORS configuration.`,
								),
							);
							return;
						}
						resolve({ partNumber: part.partNumber, etag: etag });
					}
				},
				error: (err: any) => {
					if (retriesLeft > 0) {
						const delayMs = (this.MAX_PART_RETRIES - retriesLeft + 1) * 1000;
						console.warn(
							`Upload part ${part.partNumber} failed, retrying in ${delayMs}ms (${retriesLeft} retries left)...`,
							err,
						);
						setTimeout(() => {
							this.uploadPartWithRetry(part, chunkBlob, filename, onProgress, retriesLeft - 1)
								.then(resolve)
								.catch(reject);
						}, delayMs);
					} else {
						reject(
							new Error(
								`Failed to upload part ${part.partNumber} after ${this.MAX_PART_RETRIES} attempts: ${err?.message || err}`,
							),
						);
					}
				},
			});
		});
	}

	public executeSinglePresignedUpload(file: File, payload: any): Promise<any> {
		return this.requestPresignedUploadUrl(payload).then((presigned: any) => {
			return this.uploadToPresignedUrl(
				presigned.uploadUrl,
				file,
				presigned.contentType || file.type,
				presigned.contentDisposition,
			).then(() => {
				return {
					Key: presigned.key,
					filename: presigned.filename,
				};
			});
		});
	}

	public executeMultipartUpload(file: File, payload: any): Promise<any> {
		const multipartPayload = {
			...payload,
			partSize: this.CHUNK_SIZE,
		};

		return this.requestInitiateMultipartUpload(multipartPayload).then((initResponse: any) => {
			const { uploadId, key, filename, partSize, parts } = initResponse;
			const effectivePartSize = partSize || this.CHUNK_SIZE;
			const totalParts = parts.length;
			const loadedPerPart = new Array(totalParts).fill(0);

			const updateAggregateProgress = (partIndex: number, loaded: number) => {
				loadedPerPart[partIndex] = loaded;
				const totalLoaded = loadedPerPart.reduce((acc, curr) => acc + curr, 0);
				this._httpUploadProgress(file.name, {
					loaded: Math.min(totalLoaded, file.size),
					total: file.size,
				});
			};

			return new Promise((resolve, reject) => {
				const completedParts: Array<{ partNumber: number; etag: string }> = [];
				let nextIndex = 0;
				let activeCount = 0;
				let hasFailed = false;

				const launchNext = () => {
					if (hasFailed) return;

					if (completedParts.length === totalParts) {
						completedParts.sort((a, b) => a.partNumber - b.partNumber);
						this.requestCompleteMultipartUpload({
							key,
							uploadId,
							parts: completedParts,
						})
							.then(() => {
								this._httpUploadProgress(file.name, {
									loaded: file.size,
									total: file.size,
								});
								resolve({ Key: key, filename: filename });
							})
							.catch(err => {
								this.requestAbortMultipartUpload({ key, uploadId }).catch(() => {});
								reject(err);
							});
						return;
					}

					while (activeCount < this.MAX_CONCURRENT_PARTS && nextIndex < totalParts && !hasFailed) {
						const currentIndex = nextIndex++;
						const part = parts[currentIndex];
						const start = (part.partNumber - 1) * effectivePartSize;
						const end = Math.min(start + effectivePartSize, file.size);
						const chunkBlob = file.slice(start, end);

						activeCount++;
						this.uploadPartWithRetry(
							part,
							chunkBlob,
							file.name,
							loaded => updateAggregateProgress(currentIndex, loaded),
						)
							.then(partResult => {
								activeCount--;
								completedParts.push(partResult);
								launchNext();
							})
							.catch(err => {
								if (!hasFailed) {
									hasFailed = true;
									// If any part exhausts all 4 retries, the entire multipart session must be aborted.
									// This instructs AWS S3 to immediately purge all previously uploaded parts from the
									// bucket, preventing orphan storage buildup and unnecessary AWS billing costs.
									this.requestAbortMultipartUpload({ key, uploadId }).catch(() => {});
									reject(err);
								}
							});
					}
				};

				launchNext();
			});
		});
	}

	public executeHybridUpload(file: File, payload: any): Promise<any> {
		const maxSizeBytes = this.config.getMaxFileSize() * 1024 * 1024;
		if (file && file.size > maxSizeBytes) {
			return Promise.reject({
				code: 'PTU39',
				type: 'user',
				msg: CONSTANTS.apiErrors.docUploadMaxSize,
			});
		}
		if (file.size < this.MULTIPART_THRESHOLD) {
			return this.executeSinglePresignedUpload(file, payload);
		} else {
			return this.executeMultipartUpload(file, payload);
		}
	}

	public updateDocumentPath = async function (
		orderId: string,
		propertyId: string,
		file: { name: any },
		path: any,
		name: any,
	): Promise<awsS3Service.types.UpdateDocumentPathResponse> {
		const data = {
			Property_ID: propertyId,
			Order_ID: orderId,
			File: {
				Original_Name: file.name,
				Path: path,
				Name: name,
				Type: 2,
			},
			Created_By: this.auth.getUserId(),
		};
		const httpOptions = {
			headers: new HttpHeaders({
				Authorization: 'Bearer ' + this.auth.getToken(),
			}),
		};
		const url = this.config.getBasePath() + '/orders/' + orderId + '/documentpath';

		try {
			const response = await this.http.post(url, data, httpOptions).toPromise();
			return response;
		} catch (error) {
			throw error.error;
		}
	};

	public onManagedUpload(
		file: File,
		orderId: string = '0',
		filename = UUID.UUID(),
		optimize = false,
	): Promise<awsS3Service.types.ManageUploadResponse> {
		const payload = {
			uploadType: 'order',
			orderId: orderId || '0',
			fileName: file.name,
			fileType: file.type || 'application/octet-stream',
			fileSize: file.size,
			customFileName: filename,
			optimize: !!optimize,
		};
		return this.executeHybridUpload(file, payload).then((res: any) => ({
			Bucket: '',
			Key: res.Key || res.key,
			key: res.Key || res.key,
			Location: '',
			ETag: res.ETag || '',
		}));
	}
}

